import { useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeService } from '../../../services/KnowledgeService';
import type {
  KnowledgeBaseGroupAssignment,
  KnowledgeMemberRole,
} from '../../../types/knowledge';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';

export function useAssignKnowledgeGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      userGroupId,
      role,
    }: {
      databaseId: string;
      userGroupId: string;
      role: KnowledgeMemberRole;
    }) => knowledgeService.assignGroup(databaseId, userGroupId, role),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.groupAssignments(variables.databaseId),
      });
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.members(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}

export function useUpdateKnowledgeGroupAssignmentRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      assignment,
      role,
    }: {
      databaseId: string;
      assignment: KnowledgeBaseGroupAssignment;
      role: KnowledgeMemberRole;
    }) =>
      knowledgeService.updateGroupAssignmentRole(databaseId, assignment, role),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.groupAssignments(variables.databaseId),
      });
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.members(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}

export function useUnassignKnowledgeGroupMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      assignmentId,
    }: {
      databaseId: string;
      assignmentId: string;
    }) => knowledgeService.unassignGroup(databaseId, assignmentId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.groupAssignments(variables.databaseId),
      });
      void queryClient.invalidateQueries({
        queryKey: knowledgeQueryKeys.members(variables.databaseId),
      });
      invalidateKnowledgeDatabaseSummaries(queryClient, variables.databaseId);
    },
  });
}
